import React, { useCallback, useEffect, useState } from 'react';
import { apiRequest } from '../../../utils/apiUtils';
import './OwnerRewardsPage.css';

interface RewardsVenue {
  id: string; name: string; address: string;
  isRewardsPartner: boolean; dailyValidationCap: number;
  // PENDING: se anotó y espera que la apruebe Futbolero. ACTIVE: ya valida.
  rewardsStatus: 'PENDING' | 'ACTIVE' | null;
}
interface CounterSession {
  id: string; label: string; claimedAt: string | null;
  lastUsedAt: string | null; revokedAt: string | null;
}
interface ValidationRow {
  id: string; validatedAt: string; status: string; playersCount: number;
  counterLabel: string | null;
  match: { id: string; date: string; time: string; creator: { name: string } | null } | null;
}

const OwnerRewardsPage: React.FC = () => {
  const [selected, setSelected] = useState<RewardsVenue | null>(null);
  const [pending, setPending] = useState<RewardsVenue | null>(null);
  const [sessions, setSessions] = useState<CounterSession[]>([]);
  const [validations, setValidations] = useState<ValidationRow[]>([]);
  const [newLink, setNewLink] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', address: '', city: '', province: '', dailyValidationCap: '20' });

  const loadVenues = useCallback(async () => {
    const res = await apiRequest<{ venues: RewardsVenue[] }>('/api/owner/rewards/venues');
    if (res.error) { setError(res.error); setLoading(false); return; }
    const list = res.data?.venues || [];
    setSelected(list.find((v) => v.rewardsStatus === 'ACTIVE') || null);
    setPending(list.find((v) => v.rewardsStatus === 'PENDING') || null);
    setLoading(false);
  }, []);

  useEffect(() => { loadVenues(); }, [loadVenues]);

  const loadDetail = useCallback(async (venueId: string) => {
    const [s, v] = await Promise.all([
      apiRequest<{ sessions: CounterSession[] }>(`/api/owner/rewards/counter-sessions?venueId=${venueId}`),
      apiRequest<{ validations: ValidationRow[] }>(`/api/owner/rewards/validations?venueId=${venueId}`),
    ]);
    setSessions(s.data?.sessions || []);
    setValidations(v.data?.validations || []);
  }, []);

  useEffect(() => { if (selected) loadDetail(selected.id); }, [selected, loadDetail]);

  const join = async () => {
    setError(null);
    const res = await apiRequest<{ venue: RewardsVenue }>('/api/owner/rewards/join', {
      method: 'POST',
      body: { ...form, dailyValidationCap: Number(form.dailyValidationCap) },
    });
    if (res.error) { setError(res.error); return; }
    await loadVenues();
  };

  const createSession = async () => {
    if (!selected) return;
    const res = await apiRequest<{ link: string }>('/api/owner/rewards/counter-sessions', {
      method: 'POST',
      body: { venueId: selected.id, label: 'Mostrador' },
    });
    if (res.error) { setError(res.error); return; }
    // El link se muestra una vez: después solo queda el hash en la base.
    setNewLink(res.data?.link || null);
    loadDetail(selected.id);
  };

  const revoke = async (id: string) => {
    await apiRequest(`/api/owner/rewards/counter-sessions/${id}`, { method: 'DELETE' });
    if (selected) loadDetail(selected.id);
  };

  if (loading) return <div className="rewards-page">Cargando...</div>;

  if (!selected && pending) {
    return (
      <div className="rewards-page">
        <h1>{pending.name}</h1>
        <p className="rewards-lead">
          Recibimos tu solicitud. Estamos revisando tu cancha y te avisamos cuando esté
          aprobada. Después de eso podrás generar el link del mostrador.
        </p>
      </div>
    );
  }

  if (!selected) {
    return (
      <div className="rewards-page">
        <h1>Suma tu cancha al programa</h1>
        <p className="rewards-lead">
          Los partidos que se juegan en tu cancha quedan confirmados desde tu mostrador.
          No cambia nada de cómo tomas los turnos.
        </p>
        {error && <div className="rewards-error">{error}</div>}
        <label>Nombre de la cancha
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </label>
        <label>Dirección
          <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
        </label>
        <label>Ciudad
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
        </label>
        <label>Provincia
          <input value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} />
        </label>
        <label>Turnos por día
          <input type="number" min={1} max={100} value={form.dailyValidationCap}
                 onChange={(e) => setForm({ ...form, dailyValidationCap: e.target.value })} />
        </label>
        <button className="rewards-primary" onClick={join} disabled={!form.name.trim() || !form.address.trim()}>
          Sumar mi cancha
        </button>
      </div>
    );
  }

  return (
    <div className="rewards-page">
      <h1>{selected.name}</h1>
      <p className="rewards-lead">Tope de {selected.dailyValidationCap} validaciones por día.</p>
      {error && <div className="rewards-error">{error}</div>}

      <section>
        <h2>Mostrador</h2>
        <p className="rewards-hint">
          Abre este link en el celular del mostrador. El primer celular que lo abra
          se queda con la sesión. Si lo pierdes, genera uno nuevo y da de baja el viejo.
        </p>
        <button className="rewards-primary" onClick={createSession}>Generar link de mostrador</button>
        {newLink && (
          <div className="rewards-link">
            <code>{newLink}</code>
            <button onClick={() => navigator.clipboard.writeText(newLink)}>Copiar</button>
            <p className="rewards-hint">Este link se muestra una sola vez. Guárdalo ahora.</p>
          </div>
        )}
        <ul className="rewards-sessions">
          {sessions.map((s) => (
            <li key={s.id} className={s.revokedAt ? 'revoked' : ''}>
              <span>{s.label}</span>
              <span>{s.revokedAt ? 'Dada de baja' : s.claimedAt ? 'Activa' : 'Sin usar'}</span>
              {!s.revokedAt && <button onClick={() => revoke(s.id)}>Dar de baja</button>}
            </li>
          ))}
        </ul>
      </section>

      <section>
        <h2>Partidos validados</h2>
        {validations.length === 0 ? (
          <p className="rewards-hint">Todavía no validaste ninguno.</p>
        ) : (
          <table className="rewards-table">
            <thead><tr><th>Fecha</th><th>Organiza</th><th>Jugadores</th><th>Estado</th></tr></thead>
            <tbody>
              {validations.map((v) => (
                <tr key={v.id}>
                  <td>{v.match ? `${v.match.date} ${v.match.time}` : '—'}</td>
                  <td>{v.match?.creator?.name || '—'}</td>
                  <td>{v.playersCount}</td>
                  <td>{v.status === 'VOIDED' ? 'Anulada' : 'Validada'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default OwnerRewardsPage;
